import {useEffect, useMemo} from "react";
import {Select, type SelectItemsList} from "@/primitives/Select";
import {WorkingTimeTypeCategory} from "@/shared/timrTypes";

/**
 * What the select needs of a working time type: the {@code id} it reports, the {@code name} it shows,
 * and the {@code category} it groups by. Structural on purpose — callers pass either full
 * {@code WorkingTimeType}s or the thin projection their form holds.
 */
export interface WorkingTimeTypeSelectOption {
	id: number;
	name: string;
	category: WorkingTimeTypeCategory;
}

/** The order the categories are offered in — the same one {@code DOMUtil.fillWorkingTimeTypeSelect} uses. */
const CATEGORY_ORDER: WorkingTimeTypeCategory[] = [
	WorkingTimeTypeCategory.AttendanceTime,
	WorkingTimeTypeCategory.Vacation,
	WorkingTimeTypeCategory.PaidTimeOff,
	WorkingTimeTypeCategory.UnpaidTimeOff,
	WorkingTimeTypeCategory.BreakTime,
	WorkingTimeTypeCategory.Suspension
];

interface WorkingTimeTypeSelectProps {
	id?: string;
	value: number | null;
	workingTimeTypes: WorkingTimeTypeSelectOption[];
	onValueChange: (id: number) => void;
	disabled?: boolean;
}

/**
 * A working time type {@code Select} whose items are grouped by category, the React counterpart of
 * {@code DOMUtil.fillWorkingTimeTypeSelect} — same category order, same divider between two groups,
 * so a working time type is picked the same way everywhere.
 * <p>
 * It reports the working time type it offers first while {@code value} is {@code null}.
 */
export function WorkingTimeTypeSelect({
										  id,
										  value,
										  workingTimeTypes,
										  onValueChange,
										  disabled = false
									  }: WorkingTimeTypeSelectProps) {
	// Empty categories are dropped, so the divider Select.Items puts in front of every group but the
	// first never dangles. The order *within* a group is the given one — the working time types arrive
	// alphabetically sorted from the backend, so the groups stay alphabetical.
	const groups = useMemo(
		() => CATEGORY_ORDER
			.map(category => workingTimeTypes.filter(workingTimeType => workingTimeType.category === category))
			.filter(group => group.length > 0),
		[workingTimeTypes]
	);

	// The groups stay unlabelled: the divider Select.Items puts between two of them is the whole
	// grouping the legacy select shows, which never spells the category names out.
	const items = useMemo<SelectItemsList<number>>(
		() => groups.map(group => ({items: group.map(workingTimeType => ({label: workingTimeType.name, value: workingTimeType.id}))})),
		[groups]
	);

	const firstWorkingTimeType = groups[0]?.[0];
	useEffect(() => {
		if (value === null && firstWorkingTimeType) {
			onValueChange(firstWorkingTimeType.id);
		}
	}, [value, firstWorkingTimeType]);

	return (
		<Select.Root
			items={items}
			value={value}
			// The select offers no empty item, so it never reports back a null value.
			onValueChange={selectedId => {
				if (selectedId !== null) onValueChange(selectedId);
			}}
			disabled={disabled}
		>
			<Select.Trigger id={id}/>
			<Select.Items/>
		</Select.Root>
	);
}
